import { getSignalScoreDefaults, resolveSignalRuleName } from "../utils/signalScoringUtils";
import StrategyDraftControls from "./StrategyDraftControls";
import {
  STRATEGY_LOGIC_OPTIONS,
  getStrategyByCode,
} from "../utils/strategyLogicUtils";

const MARKET_SUPPORT_OPTIONS = [
  { value: "NULLIFIED", label: "Nullified", min: 0, max: 0.99 },
  { value: "POSITIVE", label: "Positive", min: 1, max: 2.49 },
  { value: "NEUTRAL", label: "Neutral", min: 2.5, max: 4.99 },
  { value: "NEGATIVE", label: "Negative", min: 5, max: 7.49 },
  { value: "STRONG_HIT", label: "Strong Hit", min: 7.5, max: 10 },
  { value: "BEYOND_CONTROL", label: "Beyond Control", min: 10.01, max: null },
];

function getSelectedCount(group, selected) {
  return selected[group.id]?.length ?? 0;
}

function getBackendRuleForGroup(strategyRuleConfig, group) {
  return strategyRuleConfig.find((rule) => {
    const ruleName = resolveSignalRuleName({
      ruleName: rule.signalGroup,
      label: rule.signalGroup,
    });
    return ruleName === group.resolvedRuleName;
  });
}

function formatScore(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

function formatSignalText(value) {
  return String(value ?? "").replace(/_/g, " ");
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function getMarketSupportFromScore(score) {
  const number = toNumber(score);
  if (number === null) return { label: "Neutral", value: "NEUTRAL" };

  return MARKET_SUPPORT_OPTIONS.find((option) =>
    number >= option.min && (option.max === null || number <= option.max)
  ) ?? MARKET_SUPPORT_OPTIONS[MARKET_SUPPORT_OPTIONS.length - 1];
}

function formatMetricText(value) {
  return String(value ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getSelectedSignalCount(selected) {
  return Object.values(selected ?? {}).reduce((total, values) => total + (values?.length ?? 0), 0);
}

function getAppliedSourceLabel(signalLabData) {
  const applied = signalLabData?.appliedMarketBias;
  return applied?.appliedAt ?? applied?.updatedAt ?? applied?.scoreDate ?? "Latest applied";
}

function formatPreviewTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function getStrategySummary(activeStrategyLogic, signalLabData) {
  const appliedSummary = signalLabData?.appliedMarketBias?.summary;
  const selectedStrategy = signalLabData?.selectedStrategy;

  return {
    strategyName: selectedStrategy?.strategyName ?? appliedSummary?.selectedStrategyName ?? activeStrategyLogic.label,
    strategyCode: selectedStrategy?.strategyCode ?? appliedSummary?.selectedStrategyCode ?? activeStrategyLogic.code,
    globalScore: formatScore(appliedSummary?.globalScore),
    marketBias: formatSignalText(appliedSummary?.marketBias ?? "Pending"),
    riskLevel: formatSignalText(appliedSummary?.riskLevel ?? "Pending"),
    direction: formatSignalText(appliedSummary?.strategyDirection ?? activeStrategyLogic.action),
    entryRule: selectedStrategy?.entryRule ?? activeStrategyLogic.stockRule,
    source: signalLabData ? getAppliedSourceLabel(signalLabData) : "Fallback strategy logic",
    narration: signalLabData?.narrationStatus === "NARRATION_PENDING" ? "Pending" : signalLabData ? "Available" : "Not connected",
  };
}

function getSectionPreview(section) {
  const scoredItems = (section.items ?? [])
    .filter((item) => item?.adminScore !== null && item?.adminScore !== undefined)
    .slice(0, 3);

  if (!scoredItems.length) return "No scored items available";

  return scoredItems
    .map((item) => item.relatedEventName || item.sourceName || item.symbol || item.headline || item.inputId || "Input")
    .join(", ");
}

export default function SignalLogicGuide({
  signals = [],
  selected = {},
  scoringConfig = {},
  minimumScore = 60,
  strategyRuleConfig = [],
  signalLabData,
  strategy,
  selectedStrategyCode = "CUSTOM",
  onConfigChange,
  onMinimumScoreChange,
  onStrategyLogicChange,
  onApplyStrategySignals,
  onRevalidate,
  preview,
  drafts = [],
  editingDraft,
  onEditDraft,
  onSaveDraft,
  onActivateDraft,
  onSimulateDraft,
  simulatingDraftCode,
  draftSimulation,
  savingDraft,
  revalidating,
  children,
}) {
  const visibleSignals = signals
    .map((group) => ({ ...group, resolvedRuleName: resolveSignalRuleName(group) }));
  const activeStrategyLogic = getStrategyByCode(selectedStrategyCode);
  const summary = getStrategySummary(activeStrategyLogic, signalLabData);
  const strategyOptions = signalLabData?.strategies?.length
    ? signalLabData.strategies.map((option) => ({
      code: option.strategyCode,
      label: option.strategyName,
    }))
    : STRATEGY_LOGIC_OPTIONS;
  const activeRules = [];
  const inactiveRules = [];

  const updateRule = (ruleName, field, value) => {
    onConfigChange((current) => ({
      ...current,
      [ruleName]: {
        ...(current[ruleName] ?? {}),
        [field]: value,
      },
    }));
  };
  const buildRuleRow = (group) => {
    const defaults = getSignalScoreDefaults(group);
    const backendRule = getBackendRuleForGroup(strategyRuleConfig, group);
    const config = scoringConfig[group.resolvedRuleName] ?? defaults;
    const metricLabel = backendRule?.metric ?? defaults.metricLabel;
    const weight = Number(config.weight ?? defaults.weight ?? 0);
    const threshold = config.threshold ?? defaults.threshold;
    const selectedCount = selected[group.id]?.length ?? 0;
    const isActive = weight > 0 && selectedCount > 0;
    const passRule = isActive
      ? backendRule?.passRule ?? `${formatMetricText(metricLabel)} above ${threshold}`
      : "Inactive for selected strategy";
    const row = { group, config, defaults, metricLabel, passRule, weight, threshold, isActive };

    if (isActive) activeRules.push(row);
    else inactiveRules.push(row);
  };

  visibleSignals.forEach(buildRuleRow);
  const maximumScore = activeRules.reduce((total, row) => total + row.weight, 0);
  const minimumScoreNumber = Number(minimumScore);
  const minimumScoreInvalid = !Number.isFinite(minimumScoreNumber)
    || minimumScoreNumber < 0
    || minimumScoreNumber > maximumScore;

  const renderRuleRows = (rows) => rows.map(({ group, config, defaults, metricLabel, passRule, weight, threshold, isActive }) => (
    <tr className={isActive ? "is-active-rule" : "is-inactive-rule"} key={`${group.id}-${group.resolvedRuleName}`}>
      <td>
        <strong className="signal-rule-name">{group.label}</strong>
        <span className="signal-rule-key">{group.resolvedRuleName}</span>
      </td>
      <td>{group.values?.length ?? 0}</td>
      <td>{getSelectedCount(group, selected)}</td>
      <td>
        <input
          className="guide-input"
          min="0"
          type="number"
          value={config.weight ?? defaults.weight}
          onChange={(event) => updateRule(group.resolvedRuleName, "weight", event.target.value)}
        />
      </td>
      <td>
        <input
          className="guide-input"
          min="0"
          type="number"
          value={threshold}
          onChange={(event) => updateRule(group.resolvedRuleName, "threshold", event.target.value)}
        />
      </td>
      <td>{formatMetricText(metricLabel)}</td>
      <td>{passRule}</td>
      <td><input aria-label={`${group.label} core signal`} type="checkbox" checked={Boolean(config.coreSignal)} onChange={(event) => updateRule(group.resolvedRuleName, "coreSignal", event.target.checked)} /></td>
    </tr>
  ));

  return (
    <section className={`signal-guide${revalidating ? " is-revalidating" : ""}`} aria-busy={revalidating ? "true" : "false"}>
      <div className="signal-guide-header">
        <div>
          <h2>Signal Logic Guidance</h2>
          <p>Market bias chooses the plan; signal values choose the stock setup to test.</p>
        </div>
        <button className="refresh-btn" type="button" onClick={onRevalidate} disabled={revalidating || minimumScoreInvalid}>
          {revalidating ? "Running Check" : "Run Signal Check"}
        </button>
      </div>

      <div className="signal-decision-strip">
        <div>
          <span>Strategy</span>
          <strong>{summary.strategyName}</strong>
        </div>
        <div>
          <span>Market Score</span>
          <strong>{summary.globalScore}</strong>
        </div>
        <div>
          <span>Bias</span>
          <strong>{summary.marketBias}</strong>
        </div>
        <div>
          <span>Risk</span>
          <strong>{summary.riskLevel}</strong>
        </div>
        <div>
          <span>Selected Signals</span>
          <strong>{getSelectedSignalCount(selected)}</strong>
        </div>
        <div>
          <span>Source</span>
          <strong>{summary.source}</strong>
        </div>
      </div>

      <div className="signal-guide-workflow">
        <details className="signal-workflow-section collapsible-signal-section" open>
          <summary className="signal-workflow-heading">
            <span>01</span>
            <strong>Strategy Derivation</strong>
          </summary>
          {!signalLabData && (
            <div className="signal-empty-state">
              <strong>No applied market bias loaded</strong>
              <p>Signal Lab is showing fallback strategy logic until an admin-applied market bias is available.</p>
            </div>
          )}
          <div className="strategy-derivation-summary">
            <div>
              <span>Derived Strategy</span>
              <strong>{summary.strategyName}</strong>
              <p>{summary.direction}</p>
            </div>
            <div>
              <span>Stock Selection Rule</span>
              <strong>{summary.entryRule}</strong>
            </div>
            <div>
              <span>Narration</span>
              <strong>{summary.narration}</strong>
            </div>
          </div>
          <div className="market-bias-section-grid">
            {(signalLabData?.marketBiasSections ?? []).map((section) => (
              <div key={section.sectionName}>
                <span>{section.sectionName}</span>
                <strong>{formatScore(section.sectionScore)}</strong>
                <em>{getMarketSupportFromScore(section.sectionScore).label}</em>
                <p>{getSectionPreview(section)}</p>
              </div>
            ))}
          </div>
          <details className="strategy-admin-control">
            <summary>Strategy Override</summary>
            <select
              id="strategyLogicSelect"
              className="strategy-select"
              value={activeStrategyLogic.code}
              onChange={(event) => onStrategyLogicChange?.(event.target.value)}
            >
              {strategyOptions.map((option) => (
                <option key={option.code} value={option.code}>{option.label}</option>
              ))}
            </select>
          </details>
        </details>
      </div>

      <details className="advanced-signal-rules" open>
        <summary>
          <span>02</span>
          <strong>Advanced Rule Weights</strong>
        </summary>
        <div className="signal-score-controls">
          <label htmlFor="signalLabMinimumScore">
            <span>Minimum Score</span>
            <input
              id="signalLabMinimumScore"
              className="guide-input"
              min="0"
              max={maximumScore}
              type="number"
              value={minimumScore}
              onChange={(event) => onMinimumScoreChange?.(event.target.value)}
            />
          </label>
          <div>
            <span>Maximum Achievable Score</span>
            <strong>{maximumScore}</strong>
          </div>
          <p className={minimumScoreInvalid ? "signal-score-help is-error" : "signal-score-help"}>
            {minimumScoreInvalid
              ? `Enter a score between 0 and ${maximumScore}.`
              : "A stock is returned when its passed rule weights reach this score and all core rules pass."}
          </p>
        </div>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Signal</th>
                <th>Available</th>
                <th>Selected</th>
                <th>Weight</th>
                <th>Threshold</th>
                <th>Metric</th>
                <th>Pass Rule</th>
                <th>Core</th>
              </tr>
            </thead>
            <tbody>
              {renderRuleRows(activeRules)}
              {inactiveRules.length > 0 && (
                <tr className="rule-group-divider">
                  <td colSpan="8">Inactive Rules</td>
                </tr>
              )}
              {renderRuleRows(inactiveRules)}
            </tbody>
          </table>
        </div>
      </details>

      {children}

      {preview && (
        <section className="signal-check-results" aria-live="polite">
          <div className="signal-check-results-header">
            <div>
              <span>03</span>
              <strong>Signal Check Results</strong>
            </div>
            <p>Checked {formatPreviewTime(preview.previewedAt)} Â· valid until {formatPreviewTime(preview.expiresAt)}</p>
          </div>
          <div className="signal-decision-strip">
            <div><span>Matching Stocks</span><strong>{preview.candidateCount ?? 0}</strong></div>
            <div><span>Core Rejections</span><strong>{preview.coreRuleRejectedCount ?? 0}</strong></div>
            <div><span>Status</span><strong>{preview.candidateCount > 0 ? "Matches found" : "No matches"}</strong></div>
          </div>
          {(preview.warnings ?? []).map((warning) => (
            <p className="signal-check-warning" key={warning}>{warning}</p>
          ))}
          {preview.candidates?.length > 0 ? (
            <div className="guide-table-wrap">
              <table className="guide-table signal-check-candidates">
                <thead><tr><th>Stock</th><th>Sector</th><th>Price</th><th>Score</th><th>Matched Rules</th></tr></thead>
                <tbody>
                  {preview.candidates.map((stock, index) => (
                    <tr key={stock.tickerId ?? stock.symbol ?? index}>
                      <td><strong>{stock.symbol ?? stock.tickerId ?? "-"}</strong></td>
                      <td>{stock.sector ?? "-"}</td>
                      <td>{stock.currentPrice ?? stock.price ?? "-"}</td>
                      <td>{stock.score ?? stock.confidenceScore ?? 0}</td>
                      <td>{stock.matchedSignalCount ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="signal-empty-state">
              <strong>No stocks matched this configuration</strong>
              <p>Relax one or more core rules, lower count thresholds or the minimum score, widen the price range, and run the check again.</p>
            </div>
          )}
        </section>
      )}

      <StrategyDraftControls selectedStrategyCode={selectedStrategyCode} preview={preview} drafts={drafts} editingDraft={editingDraft} onEditDraft={onEditDraft} onSaveDraft={onSaveDraft} onActivateDraft={onActivateDraft} onSimulateDraft={onSimulateDraft} simulatingDraftCode={simulatingDraftCode} simulation={draftSimulation} saving={savingDraft} />

      <div className="guide-notes" />

      {revalidating && (
        <div className="logic-loading-mask signal-guide-loading-mask" role="status" aria-live="polite">
          <div className="logic-loading-panel">
            <span className="logic-loading-spinner" aria-hidden="true" />
            <strong>Revalidating stocks for this logic setup</strong>
            <p>
              Checking market bias, selected strategy rules, signal values, score thresholds, and price settings before updating the matching stock list.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
